/**
 * OocDrawer — OOC request detail + transition actions (STORY-010-004).
 *
 * Shows the request (query, language, region, masked submitter email) and the
 * status-appropriate actions: in_progress → Resolve / Won't do (with reason);
 * resolved|wont_do → Reopen. Uses the shared `BottomSheet` (focus-trapped).
 */
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { BottomSheet, Button, GPill, Skeleton } from "@garden/ui";
import { ADMIN_NS } from "@/features/admin/api";
import { useIsDesktop } from "@/features/chat/useIsDesktop";
import { useOocDetail, useTransitionOoc } from "@/features/admin/ooc/hooks";
import { WontDoForm } from "@/features/admin/ooc/WontDoForm";
import type { OocStatus } from "@/features/admin/ooc/api";

function maskEmail(email: string | null): string {
  if (!email) return "—";
  const [local, domain] = email.split("@");
  if (!domain || !local) return "•••";
  const head = local.slice(0, 2);
  return `${head}${"•".repeat(Math.max(local.length - 2, 1))}@${domain}`;
}

function statusTone(status: string): "warn" | "success" | "error" | "neutral" {
  switch (status) {
    case "in_progress":
      return "warn";
    case "resolved":
      return "success";
    case "wont_do":
      return "error";
    default:
      return "neutral";
  }
}

function FieldRow({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <dt className="text-sm text-[var(--g-secondary)]">{label}</dt>
      <dd className="text-right text-sm font-medium">{value}</dd>
    </div>
  );
}

export function OocDrawer({
  oocId,
  onClose,
}: {
  oocId: string | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const isDesktop = useIsDesktop();
  const { data: detail, isLoading } = useOocDetail(oocId);
  const transition = useTransitionOoc();
  const [wontDoMode, setWontDoMode] = useState(false);

  const isOpen = oocId !== null;

  const runTransition = (newStatus: OocStatus, reason?: string): void => {
    if (oocId === null) return;
    transition.mutate(
      { id: oocId, body: { new_status: newStatus, wont_do_reason: reason ?? null } },
      {
        onSuccess: () => {
          toast.success(t("ooc.action.success"));
          setWontDoMode(false);
          onClose();
        },
        onError: () => {
          toast.error(t("ooc.action.error"));
        },
      },
    );
  };

  return (
    <BottomSheet open={isOpen} onClose={onClose} title={t("ooc.detail.title")} desktop={isDesktop}>
      {isLoading || !detail ? (
        <div className="flex flex-col gap-3 py-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-4" data-testid="ooc-drawer-body">
          <div className="flex items-center gap-2">
            <GPill tone={statusTone(detail.status)} size="sm">
              {t(`ooc.status.${detail.status}`, { defaultValue: detail.status })}
            </GPill>
          </div>

          <section>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
              {t("ooc.detail.query")}
            </h3>
            <p
              data-testid="ooc-original-query"
              className="whitespace-pre-wrap rounded-[var(--g-radius-sm)] bg-[var(--g-elevated)] p-2 text-sm"
            >
              {detail.original_query}
            </p>
          </section>

          <dl className="divide-y divide-[var(--g-hairline)]">
            <FieldRow label={t("ooc.detail.language")} value={detail.language} />
            <FieldRow label={t("ooc.detail.region")} value={detail.region ?? "—"} />
            <FieldRow
              label={t("ooc.detail.submitterEmail")}
              value={maskEmail(detail.submitter_email)}
            />
            {detail.wont_do_reason ? (
              <FieldRow
                label={t("ooc.detail.wontDoReason")}
                value={detail.wont_do_reason}
              />
            ) : null}
          </dl>

          {/* Actions by status */}
          {wontDoMode ? (
            <WontDoForm
              pending={transition.isPending}
              onCancel={() => setWontDoMode(false)}
              onSubmit={(reason) => runTransition("wont_do", reason)}
            />
          ) : (
            <div className="flex flex-wrap justify-end gap-2" data-testid="ooc-actions">
              {detail.status === "in_progress" ? (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    data-testid="ooc-wontdo"
                    disabled={transition.isPending}
                    onClick={() => setWontDoMode(true)}
                  >
                    {t("ooc.action.wontDo")}
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    data-testid="ooc-resolve"
                    disabled={transition.isPending}
                    onClick={() => runTransition("resolved")}
                  >
                    {t("ooc.action.resolve")}
                  </Button>
                </>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  data-testid="ooc-reopen"
                  disabled={transition.isPending}
                  onClick={() => runTransition("in_progress")}
                >
                  {t("ooc.action.reopen")}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </BottomSheet>
  );
}

export default OocDrawer;
